import AppStateService from '@ajs/services/AppStateService';
import { inject, Injectable } from '@angular/core';
import { AddExcludeProductsOperationTab } from '@app/analyze-data/enums/add-exclude-products-operation-tab.enum';
import { AnalyzeDataTabQueryParam, DataValidationIssueQueryParam } from '@app/analyze-data/enums/analyze-data-query-params.enum';
import { AddExcludeProductsQueryParam, WorkspaceWorkflowQueryParam } from '@app/analyze-data/enums/workspace-workflow-query-param.enum';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { DashboardReportType } from '@app/dashboard/models/enums/dashboard-report-type.enum';

export interface NavigationLinkData {
    routerLink: string[];
    queryParams?: Record<string, string | number | boolean>;
}

@Injectable({
    providedIn: 'root'
})
export class LinkService {

    get databaseId(): string {
        return this.appStateService.getDatabaseId();
    }

    get accountId(): string {
        return this.appStateService.getAccountId();
    }

    get databasesLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'databases'];
        }
        return ['/', 'databases'];
    }

    // #region Dashboards
    get feedonomicsLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'databases'];
        }
        return ['/'];
    }

    get dashboardAccountHealthLink(): string[] {
        return ['/', this.accountId, 'dashboard', DashboardReportType.ACCOUNT_HEALTH];
    }

    get dashboardAccountOverviewReportLink(): string[] {
        return ['/', this.accountId, 'dashboard', DashboardReportType.ACCOUNT_OVERVIEW];
    }

    get dashboardStrategicBusinessReviewReportLink(): string[] {
        return ['/', this.accountId, 'dashboard', DashboardReportType.STRATEGIC_BUSINESS_REVIEW];
    }

    get feedAlertsLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feed-alerts-dashboard'];
        }
        return ['/', 'feed-alerts-dashboard'];
    }

    get feedStatusLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'status'];
        }
        return ['/', 'status'];
    }

    get feedAMPLink(): string {
        return 'https://feedamp.feedonomics.com/app/#/';
    }

    get feedSupportLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feedsupport-legacy'];
        }
        return ['/', 'feedsupport-legacy'];
    }

    get feedSupportNewLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feedsupport-new'];
        }
        return ['/', 'feedsupport-new'];
    }

    get feedSupportFormsLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feedsupport-forms'];
        }
        return ['/', 'feedsupport-forms'];
    }

    get feedSupportTicketsLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feedsupport-new', 'tickets'];
        }
        return ['/', 'feedsupport', 'tickets'];
    }

    get feedSupportMissingZohoIdLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feedsupport-new', 'missing-zoho-id'];
        }
        return ['/', 'feedsupport-new', 'missing-zoho-id'];
    }

    get feedSupportLaunchWorkspaceLink(): string[] {
        if (this.accountId) {
            return ['/', this.accountId, 'feedsupport-new', 'launch-workspace'];
        }
        return ['/', 'feedsupport-new', 'launch-workspace'];
    }

    // #endregion


    // #region Data sources
    get importsLink(): string[] {
        return ['/', this.databaseId, 'imports'];
    }

    importDetailsLink(importId: number): string[] {
        return [
            ...this.importsLink,
            `${importId}`,
            'details'
        ];
    }

    get newImportLink(): string[] {
        return ['/', this.databaseId, 'imports', 'new'];
    }

    get automatedBuildLink(): string[] {
        return ['/', this.databaseId, 'automated-feed-build'];
    }

    get automatedFeedBuildList(): string[] {
        return ['/', this.databaseId, 'automated-feed-build', 'list'];
    }

    get automatedFeedBuildNew(): string[] {
        return ['/', this.databaseId, 'automated-feed-build', 'new'];
    }

    automatedFeedBuildDetailsLink(id: string): string[] {
        return [...this.automatedBuildLink, id, 'details'];
    }

    get fileMapsLink(): string[] {
        return ['/', this.databaseId, 'file-maps'];
    }

    get extraFieldsLink(): string[] {
        return ['/', this.databaseId, 'extra-fields'];
    }

    get fieldExecutionOrderLink(): string[] {
        return ['/', this.databaseId, 'transformers_field_order'];
    }

    get ftpAccountLink(): string[] {
        return ['/', this.databaseId, 'ftp-account'];
    }

    get triggersLink(): string[] {
        return ['/', this.databaseId, 'ftp-triggers'];
    }

    // #endregion


    // #region Data
    get baseDataLink(): string[] {
        return ['/', this.databaseId, 'data'];
    }

    get dataLink(): string[] {
        return this.baseDataLink.concat(['0']);
    }

    get rawDataLink(): string[] {
        return this.baseDataLink.concat(['-1']);
    }

    getExportDataLink(exportId: string): string[] {
        return this.baseDataLink.concat([exportId]);
    }

    /** The export's Data page with the Data Validation tab open, filtered to `issues` when given. */
    getDataValidationNavigationData(exportId: string | number, issues: DataValidationIssueQueryParam[] = []): NavigationLinkData {
        return {
            routerLink: this.getExportDataLink(String(exportId)),
            queryParams: {
                analyze: AnalyzeDataTabQueryParam.DataValidation,
                ...(issues.length > 0 && { dv_issue: issues.join(',') })
            }
        };
    }

    /** The Workspace's Exclude/include products workflow, opened on its Manage Rules tab. */
    getAddExcludeManageRulesNavigationData(): NavigationLinkData {
        return {
            routerLink: this.dataLink,
            queryParams: {
                analyze: AnalyzeDataTabQueryParam.Workspace,
                workflow: WorkspaceWorkflowQueryParam.AddExcludeProducts,
                [AddExcludeProductsQueryParam.Tab]: AddExcludeProductsOperationTab.ManageRules
            }
        };
    }

    get dataOverridesLink(): string[] {
        return ['/', this.databaseId, 'data-overrides'];
    }

    get diffToolLink(): string[] {
        return ['/', this.databaseId, 'diff_tool'];
    }

    get genAILink(): string[] {
        return ['/', this.databaseId, 'genai'];
    }

    get genAICategorizationLink(): string[] {
        return ['/', this.databaseId, 'genai', 'categorization'];
    }

    get genAIAttributesLink(): string[] {
        return ['/', this.databaseId, 'genai', 'attributes'];
    }

    get channelListingsLink(): string[] {
        return ['/', this.databaseId, 'channel-listings'];
    }

    get errorOverviewLink(): string[] {
        return ['/', this.databaseId, 'error-overview'];
    }

    get errorResolutionDataLink(): string[] {
        return ['/', this.databaseId, 'error-resolution'];
    }

    get exportRunsLink(): string[] {
        return ['/', this.databaseId, 'export-runs'];
    }

    // #endregion
    // #region Transformers
    get transformersLink(): string[] {
        return ['/', this.databaseId, 'transformers'];
    }

    transformersLinkWithField(field?: string): string[] {
        if (field) {
            return this.transformersLink.concat(field);
        }
        return this.transformersLink;
    }

    get transformersNewLink(): string[] {
        return ['/', this.databaseId, 'transformers-new'];
    }

    get bulkTransformersLink(): string[] {
        return ['/', this.databaseId, 'transformers_bulk_import'];
    }

    get mapFieldLink(): string[] {
        return ['/', this.databaseId, 'map-field'];
    }

    get categoryTaxonomyLink(): string[] {
        return ['/', this.databaseId, 'category-taxonomy'];
    }

    get feedAILink(): string[] {
        return ['/', this.databaseId, 'feedai'];
    }

    get classifyTitleLink(): string[] {
        return ['/', this.databaseId, 'feedai', 'classifier'];
    }

    get attributeDataLink(): string[] {
        return ['/', this.databaseId, 'feedai', 'attributor'];
    }

    get legacyFeedAILink(): string[] {
        return ['/', this.databaseId, 'feedai', 'legacy'];
    }

    get multiChannelConfigurationLink(): string[] {
        return ['/', this.databaseId, 'feedai', 'multi-channel-configuration'];
    }

    // #endregion


    // #region Exports
    get exportsNewLink(): string[] {
        return ['/', this.databaseId, 'exports', 'new'];
    }

    /**
     * Step 2 of the taxonomy-aware add-export flow (automatedfeedbuild_workflow): region + feed type.
     * Only reachable when the chosen destination has a taxonomy mapping.
     */
    get exportsNewRegionLink(): string[] {
        return ['/', this.databaseId, 'exports', 'new', 'region'];
    }

    // Used for navigating to the appropriate create export details page from the select type page
    get exportsNewDetailsLink(): string[] {
        const preference = this.localStorageService.getItem(`new-exports-${this.appStateService.getUserId()}`);
        if (preference === 'legacy') {
            return this.exportsLegacyLink;
        }

        return ['/', this.databaseId, 'exports', 'new', 'details'];
    }

    private get baseExportsLink(): string[] {
        return ['/', this.databaseId, 'exports'];
    }

    exportDetailsLink(exportId: string): string[] {
        return [
            ...this.baseExportsLink,
            exportId,
            'details'
        ];
    }

    get exportsLegacyLink(): string[] {
        return ['/', this.databaseId, 'exports-legacy'];
    }

    get exportsLink(): string[] {
        const preference = this.localStorageService.getItem(`new-exports-${this.appStateService.getUserId()}`);
        if (preference === 'legacy') {
            return this.exportsLegacyLink;
        }

        return this.exportsListLink;
    }

    get exportsListLink(): string[] {
        return ['/', this.databaseId, 'exports', 'list'];
    }

    /**
     * Get nav link and query params for details page
     */
    getExportDetailsNavigationData(exportId: string | number): NavigationLinkData {
        const id = String(exportId);
        if (
            this.localStorageService.getItem(`new-exports-${this.appStateService.getUserId()}`) !== 'legacy'
        ) {
            return { routerLink: this.exportDetailsLink(id) };
        }

        return {
            routerLink: this.exportsLegacyLink,
            queryParams: { export_id: id, scroll: true }
        };
    }

    /**
     * Get nav link and query params for the export list page, which unpauses the given export on load.
     */
    getExportsListUnpauseNavigationData(exportId: string | number): NavigationLinkData {
        return {
            routerLink: this.exportsListLink,
            queryParams: { unpause: String(exportId) }
        };
    }

    get exportTriggersLink(): string[] {
        return ['/', this.databaseId, 'export-triggers'];
    }

    get dataGovernanceLink(): string[] {
        return ['/', this.databaseId, 'data-governance'];
    }

    get summaryGovernanceLink(): string[] {
        return ['/', this.databaseId, 'summary-governance'];
    }

    get exportRandomSamplingLink(): string[] {
        return ['/', this.databaseId, 'export-random-sampling'];
    }

    get attributeMappingLink(): string[] {
        return ['/', this.databaseId, 'exports', 'attribute-mapping'];
    }

    // #endregion

    // #region Reports Authorization
    get reportsAuthorizationLink(): string[] {
        return ['/', this.databaseId, 'reports-authorization'];
    }

    get googleAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['google']);
    }

    get googleMerchantCenterAuthorizationLink(): string[] {
        return this.googleAuthorizationLink.concat(['google-merchant-center']);
    }

    get amazonAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['amazon']);
    }

    get amazonApiAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['amazon', 'amazon-sp-api']);
    }

    get amazonShopDirectAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['amazon', 'amazon-shop-direct']);
    }

    get bingAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['bing-shopping']);
    }

    get kijijiAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['kijiji']);
    }

    get mercadoLibreAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['mercado-libre']);
    }

    get facebookAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['facebook']);
    }

    get metaAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['meta']);
    }

    get microsoftAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['microsoft']);
    }

    get wishAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['wish']);
    }

    get etsyAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['etsy']);
    }

    get ebayAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['ebay']);
    }

    get miraklAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['mirakl']);
    }

    get tiktokAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['tiktok']);
    }

    get sheinAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['shein']);
    }

    get shopifyAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['shopify']);
    }

    get walmartAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['walmart']);
    }

    get feedonomicsAuthorizationLink(): string[] {
        return this.reportsAuthorizationLink.concat(['feedonomics']);
    }

    // #endregion

    // #region Reports

    get skuReportLink(): string[] {
        return ['/', this.databaseId, 'reports-sku'];
    }

    get feedGraderLink(): string[] {
        return ['/', this.databaseId, 'qa', 'feedgrader'];
    }

    get feedQALink(): string[] {
        return ['/', this.databaseId, 'qa', 'feedqa'];
    }

    get storeSummaryReportLink(): string[] {
        return ['/', this.databaseId, 'store-summary-report'];
    }

    get dataEnrichmentAnalyticsLink(): string[] {
        return ['/', this.databaseId, 'channel-analytics'];
    }

    get dataEnrichmentAnnotationsLink(): string[] {
        return ['/', this.databaseId, 'data-enrichment-annotations-report'];
    }

    // #endregion


    // #region Right nav
    get logsLink(): string[] {
        return ['/', this.databaseId, 'logs'];
    }

    get logsNewLink(): string[] {
        return ['/', this.databaseId, 'logs-new'];
    }

    get logsChangeLink(): string[] {
        return ['/', this.databaseId, 'logs-new', 'change'];
    }

    get oldAlertsLink(): string[] {
        return ['/', this.databaseId, 'alerts-old'];
    }

    get databaseAlerts(): string[] {
        return ['/', this.accountId, 'alerts', 'databases'];
    }

    get databaseManageAlerts(): string[] {
        return ['/', this.accountId, 'alerts', 'databases', 'manage'];
    }

    get databaseManageSubscriberAlerts(): string[] {
        return ['/', this.accountId, 'alerts', 'databases', 'subscribers', 'manage'];
    }

    get commentsLink(): string[] {
        return ['/', this.databaseId, 'comments'];
    }

    get vaultLink(): string[] {
        return ['/', this.databaseId, 'vault'];
    }

    get accountsLink(): string[] {
        return ['/', 'accounts'];
    }

    get profileLink(): string[] {
        return ['/', 'profile'];
    }

    get billingLink(): string[] {
        return ['/', 'billing'];
    }

    get userAccessControlLink(): string[] {
        return ['/', this.accountId, 'users'];
    }

    get companionSettingsLink(): string[] {
        return ['/', this.accountId, 'companion-settings'];
    }

    get releaseNotesLink(): string[] {
        return ['/', 'release-notes'];
    }

    get utilitiesLink(): string[] {
        return ['/', 'utilities'];
    }

    get logoutLink(): string[] {
        return ['/auth_redirect'];
    }

    get cheatSheetLink(): string[] {
        return ['/', 'help', 'cheat-sheet'];
    }

    get keyboardShortcutLink(): string[] {
        return ['/', 'keyboard-shortcuts'];
    }

    get feedAcademyLink(): string {
        if (this.appStateService.user?.isInternal()) {
            return 'https://internal.bigcommerceuniversity.com';
        }

        return 'https://feedacademy.bigcommerceuniversity.com';
    }

    get ahaLink(): string {
        return 'https://feedonomics.ideas.aha.io/';
    }

    // #endregion


    // #region eBay Products
    get ebaySelectionLink(): string[] {
        return ['/', 'ebay-selection'];
    }

    // #endregion

    // #region self-service-help
    get selfServiceHelp(): string[] {
        return ['/', this.databaseId, 'bc-support'];
    }

    /**
     * This link directs the user directly to the BigCommerce support page for Feedonomics Self Service
     */
    get bcSelfServiceSupportLink(): string {
        return 'https://support.bigcommerce.com/s/article/Feedonomics-Self-Service-Apps?language=en_US';
    }

    get bcLiveChatLink(): string {
        return 'https://support.bigcommerce.com/apex/SupportLiveAgentPreChatPage';
    }

    get bcSelfServiceFAQ(): string {
        return 'https://support.bigcommerce.com/s/article/Feedonomics-Self-Service-Apps';
    }

    // #endregion

    /**
     * Note: this is the base route, but requires query parameters
     */
    get connectChannelAuthLink(): string[] {
        return ['/', this.databaseId, 'connect', 'auth'];
    }

    /**
     * Get nav link and query params that send the user through the channel auth flow for the given export.
     */
    getConnectChannelAuthNavigationData(channel: string, vaultId: number, exportId: string | number): NavigationLinkData {
        return {
            routerLink: this.connectChannelAuthLink,
            queryParams: {
                channel,
                vault_id: vaultId,
                export_id: String(exportId)
            }
        };
    }

    /**
     * Note: this is the base route, but requires query parameters
     */
    get connectChannelConfigLink(): string[] {
        return ['/', this.databaseId, 'connect', 'config'];
    }

    /**
     * Note: this is the base route, but requires query parameters
     */
    get connectChannelExportUrlLink(): string[] {
        return ['/', this.databaseId, 'connect', 'export-url'];
    }

    /**
     * Get nav link and query params that send the user to the Export URL page for the given export.
     */
    getConnectChannelExportUrlNavigationData(channel: string, exportId: string | number): NavigationLinkData {
        return {
            routerLink: this.connectChannelExportUrlLink,
            queryParams: {
                channel,
                export_id: String(exportId)
            }
        };
    }

    /**
     * Note: this is the base route, but requires query parameters
     */
    get connectChannelAnalyticsLink(): string[] {
        return ['/', this.databaseId, 'connect', 'analytics'];
    }

    get feedAmpPageLink(): string[] {
        return ['/', this.databaseId, 'feedamp'];
    }

    private readonly appStateService: AppStateService = inject(AppStateService);
    private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
}
